export type ColumnSortDirection = 'asc' | 'desc'

export type ColumnSort<Column extends string> = {
  column: Column
  direction: ColumnSortDirection
}

export const isColumnSortDirection = (
  value: string
): value is ColumnSortDirection => value === 'asc' || value === 'desc'

export const parseColumnSort = <Column extends string>(
  column: string | null,
  direction: string | null,
  options: {
    columns: readonly Column[]
    defaultSort: ColumnSort<Column>
    defaultDirection: Record<Column, ColumnSortDirection>
  }
): ColumnSort<Column> => {
  if (!column || !isColumn(column, options.columns)) {
    return options.defaultSort
  }

  if (direction && isColumnSortDirection(direction)) {
    return { column, direction }
  }

  return { column, direction: options.defaultDirection[column] }
}

export const isDefaultColumnSort = <Column extends string>(
  sort: ColumnSort<Column>,
  defaultSort: ColumnSort<Column>
) =>
  sort.column === defaultSort.column &&
  sort.direction === defaultSort.direction

export const nextColumnSort = <Column extends string>(
  current: ColumnSort<Column>,
  column: string,
  options: {
    isColumn: (value: string) => value is Column
    defaultDirection: Record<Column, ColumnSortDirection>
  }
): ColumnSort<Column> => {
  if (!options.isColumn(column)) {
    return current
  }

  if (current.column === column) {
    return {
      column,
      direction: current.direction === 'asc' ? 'desc' : 'asc',
    }
  }

  return { column, direction: options.defaultDirection[column] }
}

export const appendSortParams = <Column extends string>(
  params: URLSearchParams,
  sort: ColumnSort<Column>,
  defaultSort: ColumnSort<Column>
) => {
  if (isDefaultColumnSort(sort, defaultSort)) {
    return
  }

  params.set('sort', sort.column)
  params.set('direction', sort.direction)
}

const isColumn = <Column extends string>(
  value: string,
  columns: readonly Column[]
): value is Column => (columns as readonly string[]).includes(value)
